import { useQueryClient } from '@tanstack/react-query';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  ApiError,
  advanceAuthGeneration,
  api,
  getAuthGeneration,
  subscribeUnauthorized,
} from './api';
import { clearChatDrafts } from './chatDrafts';
import { clearPendingSends } from './pendingSend';
import { activateProfilePreferences, deactivateProfilePreferences } from './settings';

/**
 * Auth state. Three pieces of info are worth caching in React state:
 *   - `user` — who is currently signed in, or null
 *   - `status` — so the UI can distinguish "still checking" from "signed out"
 *   - `error` — surfaced to the login form
 *
 * The JWT lives in an httpOnly cookie set by the server at /login or
 * /register; JS never reads or writes it. On mount we just hit
 * /api/auth/me — if the cookie authenticates, we're signed in.
 */

export type User = {
  id: number;
  username: string;
  is_admin: boolean;
  /** Model ids this user may run, or null = no restriction. */
  allowed_models: string[] | null;
  /** Admin with the Mac connected: may create «Мак» chats. */
  mac_available?: boolean;
  /** Admin with «Экран Мака» configured on the server. */
  mac_screen_available?: boolean;
};

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

type LoginResult = { user: User };
/** Self-service registration no longer logs in — it returns the
 * assigned workspace<N> login and leaves the request pending. */
export type RegisterResult = { login: string };
type StatusResult = {
  registration_open: boolean;
  bootstrap_needed: boolean;
};

type AuthContextValue = {
  status: AuthStatus;
  user: User | null;
  serverStatus: StatusResult | null;
  login: (username: string, password: string) => Promise<void>;
  register: (realName: string, password: string) => Promise<RegisterResult>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [serverStatus, setServerStatus] = useState<StatusResult | null>(null);
  const currentUserId = useRef<number | null>(null);

  const dropSession = useCallback(() => {
    advanceAuthGeneration();
    // Keys are per-user, but messages use staleTime: Infinity — wipe
    // the singleton cache on every identity change so the next profile
    // cannot read the previous tenant's sidebar/chat.
    clearChatDrafts();
    clearPendingSends();
    qc.clear();
    deactivateProfilePreferences();
    currentUserId.current = null;
    setUser(null);
    setStatus('anonymous');
  }, [qc]);

  useEffect(() => subscribeUnauthorized(dropSession), [dropSession]);

  useEffect(() => {
    if (status !== 'authenticated' || user?.id == null) return;
    const expectedId = user.id;
    const check = () => {
      if (document.visibilityState !== 'visible') return;
      const expectedGeneration = getAuthGeneration();
      void api<{ user: User }>('/auth/me')
        .then((me) => {
          // A login/logout may have happened while this request was in flight.
          if (currentUserId.current !== expectedId || getAuthGeneration() !== expectedGeneration)
            return;
          if (me.user.id !== expectedId) {
            advanceAuthGeneration();
            clearChatDrafts();
            clearPendingSends();
            qc.clear();
            activateProfilePreferences(me.user.id);
          }
          currentUserId.current = me.user.id;
          setUser(me.user);
        })
        .catch(() => {});
    };
    const timer = window.setInterval(check, 30_000);
    document.addEventListener('visibilitychange', check);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', check);
    };
  }, [status, user?.id, qc]);

  // Boot-time check. Runs once. The cookie auths silently; we just
  // ping /me to see if we're someone.
  useEffect(() => {
    // One-time cleanup: older builds kept a Bearer JWT here. Drop it
    // so leftover state can't confuse anyone poking at DevTools.
    try {
      localStorage.removeItem('mc.token');
    } catch {
      /* private-mode etc. */
    }
    let cancelled = false;
    const bootGeneration = getAuthGeneration();
    (async () => {
      // Fetch public status (to know bootstrap_needed, registration_open)
      try {
        const s = await api<StatusResult>('/auth/status');
        if (!cancelled) setServerStatus(s);
      } catch {
        // If even the status endpoint fails, the server's probably down;
        // we'll show login and let the user retry.
      }

      if (cancelled || getAuthGeneration() !== bootGeneration) return;
      try {
        const me = await api<{ user: User }>('/auth/me');
        if (cancelled || getAuthGeneration() !== bootGeneration) return;
        if (currentUserId.current !== me.user.id) {
          advanceAuthGeneration();
          clearChatDrafts();
          clearPendingSends();
        }
        activateProfilePreferences(me.user.id);
        currentUserId.current = me.user.id;
        setUser(me.user);
        setStatus('authenticated');
      } catch (err) {
        if (cancelled || getAuthGeneration() !== bootGeneration) return;
        if (!(err instanceof ApiError) || (err.status !== 401 && err.status !== 403)) {
          console.warn('[auth] unexpected /me error', err);
        }
        setStatus('anonymous');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(
    async (username: string, password: string) => {
      const result = await api<LoginResult>('/auth/login', {
        method: 'POST',
        body: { username, password },
      });
      advanceAuthGeneration();
      clearChatDrafts();
      clearPendingSends();
      qc.clear();
      activateProfilePreferences(result.user.id);
      currentUserId.current = result.user.id;
      setUser(result.user);
      setStatus('authenticated');
    },
    [qc],
  );

  const register = useCallback(
    async (realName: string, password: string) => {
      // Two server behaviours share this endpoint:
      //  - bootstrap (first run): responds with {user, login} AND sets the
      //    auth cookie → we log in immediately.
      //  - self-service: responds with {login} only, no cookie → the
      //    account is pending admin approval, we stay anonymous and hand
      //    the assigned login back to the caller to display.
      const result = await api<{ user?: User; login: string }>('/auth/register', {
        method: 'POST',
        body: { real_name: realName, password },
      });
      if (result.user) {
        advanceAuthGeneration();
        clearChatDrafts();
        clearPendingSends();
        qc.clear();
        activateProfilePreferences(result.user.id);
        currentUserId.current = result.user.id;
        setUser(result.user);
        setStatus('authenticated');
      }
      return { login: result.login };
    },
    [qc],
  );

  const logout = useCallback(async () => {
    // The server owns the HttpOnly cookie and session revocation. A failed
    // request is not a logout; keep the current profile visible for retry.
    const generation = getAuthGeneration();
    await api('/auth/logout', { method: 'POST' });
    if (generation === getAuthGeneration()) dropSession();
  }, [dropSession]);

  return (
    <AuthContext.Provider value={{ status, user, serverStatus, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth() must be used inside <AuthProvider>');
  return ctx;
}
